import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Partner, PartnerSearchHit } from '@marageti/z4-sdk/lib/travel';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { SearchRequest, SearchResult } from '@marageti/z4-sdk/lib/entity';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import NonCompeteEditView from './non-compete-edit-view';

const partnerFromSearchHit = (hit: PartnerSearchHit): Partner => ({ id: hit.id, name: hit.name } as Partner);

const NonCompeteEditContainer = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const apiClient = useApiClient();
  const { partnerId } = useParams<{ partnerId: string }>();

  const { data: partner, mutate } = useFetch<Partner>(apiClient.partnersClient.getPartner, [partnerId], true);

  const partnerCountries = useMemo(() => partner?.countries?.map((c) => c.country) || [], [partner]);
  const [submissionError, setSubmissionError] = useState<ApiError>();

  const searchRequest: SearchRequest = useMemo(() => ({
    filterBy: [{
      field: 'countries.country',
      value: partnerCountries,
    }],
    pagination: {
      limit: 1000,
      page: 1,
    },
    sortBy: [{
      field: 'name',
      order: 'asc',
    }],
  }), [partnerCountries]);

  const { data: partners } = useFetch<SearchResult<PartnerSearchHit>>(
    apiClient.partnersClient.postPartnerSummarySearch,
    [searchRequest],
    partnerCountries.length > 0,
  );

  const noCompetePartners = useMemo(() => {
    if (!partners || !partner) return [];
    const noCompeteIds = Object.keys(partner.noCompete);
    return partners.hits
      .filter((possiblePartner) => (
        possiblePartner.id !== partner.id && !noCompeteIds.includes(possiblePartner.id)
      ))
      .map(partnerFromSearchHit);
  }, [partners, partner]);

  const addNoCompete = async (noCompetePartnerId: string) => {
    try {
      await apiClient.partnersClient.postPartnerNoCompete(partnerId!, noCompetePartnerId);
      mutate();
      enqueueSnackbar('Non-compete Added.', { variant: 'success' });
      navigate(-1);
    } catch (err) {
      setSubmissionError(err as ApiError);
      enqueueSnackbar('Adding non-compete partner failed.', { variant: 'default' });
    }
  };

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    if (partnerId) {
      addNoCompete(data.noCompetePartnerId);
    }
  };

  const onCancel = () => navigate(-1);

  return (
    <NonCompeteEditView
      onCancel={onCancel}
      onSubmit={onSubmit}
      partners={noCompetePartners}
      submissionError={submissionError}
    />
  );
};

export default NonCompeteEditContainer;
